import PropTypes from "prop-types";
import moment from "moment";
import { useRef, useLayoutEffect } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  CircularProgress,
  Grid,
  TextField,
  Typography,
  InputAdornment,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import InlineMessage from "../../../common/InlineMessage/InlineMessage";
import { MandatoryField } from "../../../common/MandatoryFieldsIcon/MandatoryField";
import { formatCurrency } from "../adManagementFormHelpers";
import { formikPropType } from "./common/adManagementPropTypes";
import ContentRedirectOption from "./ContentRedirectOption/ContentRedirectOption";
import { isSuperAdminOrAdminRole } from "../../../utilities/UserRoleUtils";

/* Scheduling rules:
 *  • Start Date — today or later (past disabled).
 *  • End Date   — between Start Date and Start Date + 90 days. */
const MAX_RANGE_DAYS = 90;

/* True when `date` is found (day-precision) inside `list`. Tolerates a
 * non-array `list` by returning false. */
const isDateInList = (date, list) => {
  if (!Array.isArray(list) || list.length === 0) return false;
  return list.some((d) => moment(date).isSame(d, "day"));
};

/* Sets the new Start Date and clears the End Date when it would fall outside
 * the allowed [start, start + 90 days] window. Extracted to a helper to keep
 * the DatePicker's inline onChange small. */
const applyStartDateChange = (formik, val) => {
  formik.setFieldValue("startDate", val);
  const end = formik.values.endDate;
  if (!val || !end) return;
  const maxEnd = moment(val).add(MAX_RANGE_DAYS, "days");
  const outOfWindow =
    moment(end).isBefore(val, "day") || moment(end).isAfter(maxEnd, "day");
  if (outOfWindow) formik.setFieldValue("endDate", null);
};

/* Currency masking - removes non-digits except first dot, limits to 2 decimals, strips leading zeros */
const maskCurrency = (raw) => {
  if (raw === "" || raw === null || raw === undefined) return "";
  const cleaned = String(raw).replaceAll(/[^\d.]/g, "");
  const [whole, ...rest] = cleaned.split(".");

  /* Strip leading zeros from whole part, but keep at least "0" */
  const wholeWithoutLeadingZeros = whole.replace(/^0+/, "") || "0";

  if (rest.length === 0) return wholeWithoutLeadingZeros;
  return `${wholeWithoutLeadingZeros}.${rest.join("").slice(0, 2)}`;
};

/* Format currency value with 2 decimals - handles both string and numeric inputs, strips leading zeros */
const formatCurrencyDisplay = (val) => {
  if (val === "" || val === null || val === undefined) return "";

  /* Convert to number to handle leading zeros and ensure proper formatting */
  const n = Number(val);
  if (!Number.isFinite(n)) return "";

  /* Format with exactly 2 decimals */
  return n.toFixed(2);
};

/* Resolves the error string passed to <ContentRedirectOption>. Returns the
 * first relevant field error, or "" when none should be surfaced yet. */
const resolveContentRedirectError = (formik) => {
  const shouldShow =
    formik.submitCount > 0 || formik.touched.ContentRedirectType;
  if (!shouldShow) return "";
  return (
    formik.errors.ContentRedirectType ||
    formik.errors.externalUrl ||
    formik.errors.linkedContentIds ||
    ""
  );
};

/* Returns the formik error string for `field` only when it has been touched,
 * else "". Keeps inline JSX free of && / ternary chains. */
const getFieldError = (formik, field) =>
  (formik.touched[field] && formik.errors[field]) || "";

const getEndMaxDate = (startDate) =>
  startDate ? moment(startDate).add(MAX_RANGE_DAYS, "days") : undefined;

const shouldShowDiscountedCostField = ({
  showDiscountedCost,
  isNonAdmin,
  discountedAmount,
}) => {
  if (!showDiscountedCost) return false;
  if (!isNonAdmin) return true;
  const amount = Number(String(discountedAmount ?? "").replaceAll(",", ""));
  return Number.isFinite(amount) && amount > 0;
};

const SchedulingSection = ({
  accordionSx,
  formik,
  isViewMode,
  isLimited,
  estimatedCost,
  isCalculatingCost,
  additionalCharge,
  isCalculatingDelta,
  showAdditionalCharge,
  showDiscountedCost,
  distributorId,
  unavailableDates,
  availabilityInfo,
}) => {
  const today = moment().startOf("day");
  const endMaxDate = getEndMaxDate(formik.values.startDate);
  const shouldDisableDate = (date) => isDateInList(date, unavailableDates);
  const isAdminOrSuperAdmin = isSuperAdminOrAdminRole();
  const isNonAdmin = !isAdminOrSuperAdmin;
  const shouldRenderDiscountedCost = shouldShowDiscountedCostField({
    showDiscountedCost,
    isNonAdmin,
    discountedAmount: formik.values.discountedAmount,
  });
  const rowItemMd = shouldRenderDiscountedCost ? 3 : 4;

  /* Caret preservation for currency input - prevents cursor jumping to end */
  const costInputRef = useRef(null);
  const caretRef = useRef(null);

  useLayoutEffect(() => {
    if (caretRef.current === null || !costInputRef.current) return;
    const pos = caretRef.current;
    caretRef.current = null;
    try {
      costInputRef.current.setSelectionRange(pos, pos);
    } catch {
      /* setSelectionRange unsupported — ignore */
    }
  });

  const handleDiscountedAmountInput = (e) => {
    const el = e.target;
    const raw = el.value;
    const selStart = el.selectionStart ?? raw.length;
    const masked = maskCurrency(raw);
    /* Count valid chars (digits/dots) before cursor */
    const keptBeforeCaret = raw
      .slice(0, selStart)
      .replaceAll(/[^\d.]/g, "").length;
    caretRef.current = Math.min(keptBeforeCaret, masked.length);
    /* Store raw masked input without formatting — formatting happens on blur */
    formik.setFieldValue("discountedAmount", masked);
  };
  const isCostEditable = !isAdminOrSuperAdmin || isCalculatingCost;
  return (
    <Accordion sx={accordionSx} className="app-accordion">
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography fontWeight={700}>Scheduling</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ backgroundColor: "#f8fcff" }}>
        {availabilityInfo?.unavailableReason && (
          <Alert
            severity="warning"
            sx={{
              mb: 2,
              backgroundColor: "#FFF8E1",
              border: "1px solid #FFE0B2",
              "& .MuiAlert-message": { fontSize: 14 },
            }}
          >
            {availabilityInfo?.unavailableReason}
          </Alert>
        )}
        {/* Row 1: dates + estimated cost */}
        <Grid container spacing={2} sx={{ mb: 2, pt: 1 }}>
          {/* Start Date */}
          <Grid item xs={12} sm={6} md={rowItemMd} sx={{ padding: 2 }}>
            <label
              style={{
                display: "block",
                marginBottom: 5,
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              Start Date <MandatoryField />
            </label>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DesktopDatePicker
                inputFormat="MM/DD/YYYY"
                value={formik.values.startDate}
                disabled={isViewMode || isLimited}
                minDate={today}
                shouldDisableDate={shouldDisableDate}
                onChange={(val) => applyStartDateChange(formik, val)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    size="small"
                    onBlur={() => formik.setFieldTouched("startDate", true)}
                    sx={
                      isViewMode || isLimited
                        ? {
                            "& .MuiInputBase-root": {
                              backgroundColor: "#f5f5f5",
                            },
                          }
                        : {}
                    }
                  />
                )}
              />
            </LocalizationProvider>
            <InlineMessage error={getFieldError(formik, "startDate")} />
          </Grid>

          {/* End Date */}
          <Grid item xs={12} sm={6} md={rowItemMd} sx={{ padding: 2 }}>
            <label
              style={{
                display: "block",
                marginBottom: 5,
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              End Date <MandatoryField />
            </label>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DesktopDatePicker
                inputFormat="MM/DD/YYYY"
                value={formik.values.endDate}
                disabled={isViewMode || isLimited || !formik.values.startDate}
                minDate={formik.values.startDate || today}
                maxDate={endMaxDate}
                shouldDisableDate={shouldDisableDate}
                onChange={(val) => formik.setFieldValue("endDate", val)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    size="small"
                    onBlur={() => formik.setFieldTouched("endDate", true)}
                    sx={
                      isViewMode || isLimited
                        ? {
                            "& .MuiInputBase-root": {
                              backgroundColor: "#f5f5f5",
                            },
                          }
                        : {}
                    }
                  />
                )}
              />
            </LocalizationProvider>
            <InlineMessage error={getFieldError(formik, "endDate")} />
          </Grid>

          {/* Cost slot — same row, same grid item width as Start / End Date.
           * Shows EITHER the base "Estimated Cost" (normal flow) OR the
           * "Additional Charge" (purchased-ad upgrade flow). They never
           * appear together, so they share one slot to keep the row
           * balanced. */}
          {showAdditionalCharge ? (
            <Grid item xs={12} sm={6} md={rowItemMd} sx={{ padding: 2 }}>
              <label
                htmlFor="ad-additional-charge"
                style={{ display: "block" }}
              >
                <span
                  style={{
                    display: "block",
                    marginBottom: 5,
                    fontSize: 14,
                    fontWeight: 500,
                  }}
                >
                  Additional Charge
                </span>
                {isCalculatingDelta ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-start",
                      height: 38,
                      px: 1.5,
                      backgroundColor: "#f5f5f5",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                    }}
                    role="status"
                    aria-label="Calculating additional charge"
                  >
                    <CircularProgress size={20} />
                  </Box>
                ) : (
                  <input
                    id="ad-additional-charge"
                    className="form-label form-control"
                    value={formatCurrency(additionalCharge ?? 0)}
                    disabled
                    readOnly
                    style={{ backgroundColor: "#f5f5f5" }}
                  />
                )}
              </label>
            </Grid>
          ) : (
            <Grid item xs={12} sm={6} md={rowItemMd} sx={{ padding: 2 }}>
              <Typography
                component="div"
                style={{
                  display: "block",
                  marginBottom: 5,
                  fontSize: 14,
                  fontWeight: 500,
                }}
              >
                Estimated Cost <MandatoryField />
              </Typography>
              {isCalculatingCost ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    height: 40,
                    px: 1.5,
                    backgroundColor: "#f5f5f5",
                    border: "1px solid #ced4da",
                    borderRadius: "4px",
                  }}
                >
                  <CircularProgress size={20} />
                </Box>
              ) : (
                <input
                  id="ad-estimated-cost"
                  aria-label="Estimated Cost"
                  className="form-label form-control"
                  value={
                    estimatedCost > 0 ? formatCurrency(estimatedCost) : "—"
                  }
                  disabled
                  readOnly
                  style={{
                    backgroundColor: "#f5f5f5",
                    color: isNonAdmin ? "#00000061" : undefined,
                  }}
                />
              )}
            </Grid>
          )}
          {shouldRenderDiscountedCost && (
            <Grid item xs={12} sm={6} md={3} sx={{ px: 2 }}>
              <Typography
                component="div"
                style={{
                  display: "block",
                  marginBottom: 5,
                  fontSize: 14,
                  fontWeight: 600,
                  color: "green",
                }}
              >
                Discounted Cost
              </Typography>
              <TextField
                id="ad-discounted-cost"
                fullWidth
                size="small"
                type="text"
                inputMode="decimal"
                inputRef={costInputRef}
                value={formatCurrencyDisplay(formik.values.discountedAmount)}
                onChange={handleDiscountedAmountInput}
                onBlur={() => {
                  formik.setFieldTouched("discountedAmount", true);
                  const formatted = formatCurrencyDisplay(
                    formik.values.discountedAmount,
                  );
                  formik.setFieldValue("discountedAmount", formatted);
                }}
                placeholder="Enter Discounted Cost"
                disabled={isViewMode || isCostEditable}
                InputProps={{
                  placeholder: "Enter Discounted Cost",
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
                inputProps={{
                  "aria-label": "Discounted Cost",
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: 40,
                    backgroundColor:
                      isViewMode || isCostEditable ? "#f5f5f5" : "transparent",
                  },
                  "& .MuiOutlinedInput-root.Mui-disabled": {
                    "& input": {
                      border: "none !important",
                      padding: "7.5px 14px",
                      color: "#000",
                      WebkitTextFillColor: "#000",
                    },
                  },
                }}
              />
              <InlineMessage
                error={getFieldError(formik, "discountedAmount")}
              />
            </Grid>
          )}
        </Grid>

        {/* Row 2: content redirect radio buttons */}
        <ContentRedirectOption
          value={formik.values.ContentRedirectType}
          onChange={(next) => formik.setFieldValue("ContentRedirectType", next)}
          externalUrl={formik.values.externalUrl}
          onExternalUrlChange={(externalUrl) =>
            formik.setFieldValue("externalUrl", externalUrl)
          }
          selectedContent={formik.values.linkedContentItems}
          onSelectedContentChange={(items) => {
            const safeItems = items ?? [];
            /* Persist the full selection objects so re-opening the dialog
             * pre-checks the previously-chosen rows. */
            formik.setFieldValue("linkedContentItems", safeItems);
            /* Mirror integer ids for validation + API payload. */
            formik.setFieldValue(
              "linkedContentIds",
              safeItems
                .map((entry) => Number(entry?.id))
                .filter((n) => Number.isInteger(n) && n > 0),
            );
          }}
          distributorId={distributorId}
          isViewMode={isViewMode}
          error={resolveContentRedirectError(formik)}
        />
      </AccordionDetails>
    </Accordion>
  );
};

SchedulingSection.propTypes = {
  accordionSx: PropTypes.object.isRequired,
  formik: formikPropType,
  isViewMode: PropTypes.bool.isRequired,
  isLimited: PropTypes.bool.isRequired,
  estimatedCost: PropTypes.number.isRequired,
  isCalculatingCost: PropTypes.bool,
  additionalCharge: PropTypes.number,
  isCalculatingDelta: PropTypes.bool,
  showAdditionalCharge: PropTypes.bool,
  showDiscountedCost: PropTypes.bool,
  distributorId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  remainingDays: PropTypes.number,
  contentRedirectLabel: PropTypes.string,
  unavailableDates: PropTypes.array,
  availabilityInfo: PropTypes.shape({
    isAvailable: PropTypes.bool,
    occupiedSlots: PropTypes.number,
    maxSlots: PropTypes.number,
    unavailableReason: PropTypes.string,
    nextAvailableDate: PropTypes.string,
  }),
};

SchedulingSection.defaultProps = {
  isCalculatingCost: false,
  additionalCharge: 0,
  isCalculatingDelta: false,
  showAdditionalCharge: false,
  showDiscountedCost: false,
  remainingDays: 0,
  contentRedirectLabel: "",
  unavailableDates: [],
  availabilityInfo: null,
};

export default SchedulingSection;
